import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Divider,
  Button,
  ListItem,
  ListItemText,
  ListItemIcon,
  MenuItem,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
// import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";

import { useAppLocation } from "agent_location_context";
import { useAwaitDialog } from "app/components/status_dialog";

import GoogleAddressField from "app/components/google_address";

function VisitLocationOnboardingPage() {
  const pageTitle = "Visit Tasks - Onboarding";
  usePageTitle(pageTitle);

  const { ref } = useParams();
  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const { applocation, permissionStatus, updateAppLocation } = useAppLocation();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [statesLgas, setStatesLgas] = useState([]);
  const [lgas, setLgas] = useState([]);

  const [locationTitle, setLocationTitle] = useState(null);
  const [locationPhoneNumber, setLocationPhoneNumber] = useState(null);
  const [locationAddress, setLocationAddress] = useState(null);
  const [locationState, setLocationState] = useState(null);
  const [locationLga, setLocationLga] = useState(null);
  const [locationCity, setLocationCity] = useState(null);
  const [locationLat, setLocationLat] = useState(null);
  const [locationLng, setLocationLng] = useState(null);
  const [locationRad, setLocationRad] = useState(100);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agent/visits/trackers/${ref}/onboard/init.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setStatesLgas(result.data.data.states_lgas);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!locationState) {
      setLgas([]);
      return;
    }

    setLocationLga(null);
    const state = statesLgas.find((item) => item.reference === locationState);
    setLgas(state ? state.lgas : []);
  }, [locationState]);

  async function createLocation() {
    if (locationTitle === null) {
      setSeverity("error");
      setErrMsg("A location name is required.");
      setOpen(true);
      return;
    }

    if (locationAddress === null) {
      setSeverity("error");
      setErrMsg("A location address is required.");
      setOpen(true);
      return;
    }

    if (locationLat === null) {
      setSeverity("error");
      setErrMsg("Location latitude is required");
      setOpen(true);
      return;
    }

    if (locationLng === null) {
      setSeverity("error");
      setErrMsg("Location longitude is required");
      setOpen(true);
      return;
    }

    if (locationCity === null) {
      setSeverity("error");
      setErrMsg("City is required");
      setOpen(true);
      return;
    }

    if (locationState === null) {
      setSeverity("error");
      setErrMsg("State is required");
      setOpen(true);
      return;
    }

    if (locationLga === null) {
      setSeverity("error");
      setErrMsg("LGA is required");
      setOpen(true);
      return;
    }

    setShowLoading(true);
    const json = {
      reference: ref,
      title: locationTitle,
      address: locationAddress,
      phone_number: locationPhoneNumber,
      latitude: locationLat,
      longitude: locationLng,
      city: locationCity,
      state: locationState,
      lga: locationLga,
      lat: applocation?.lat,
      lng: applocation?.lng,
    };

    console.log(json);

    await axios
      .post("agent/visits/trackers/onboard.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(
            result.data.data.message,
            "success",
            "Success!",
          );

          navigate(`/visits/locations/${result.data.data.reference}`);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Error!");
        }
      })
      .catch(async () => {
        setOpen(true);
        await statusDialog.show(
          `Unable to complete request. ${
            navigator.onLine
              ? ""
              : "Make sure you have an active internet connection"
          }`,
          "success",
          "Success!",
        );
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mt={1} mb={5}>
        <MDBox ml={-2}>
          <Button
            onClick={() => navigate(`/visits/trackers/${ref}`)}
            startIcon={<Icon>west</Icon>}
            color="dark"
          >
            Back to locations
          </Button>
        </MDBox>
        {pageLoaded && (
          <MDBox mt={1}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h6">Onboard New Location</MDTypography>
                <MDBox mt={3}>
                  <MDInput
                    value={locationTitle}
                    name="title"
                    margin="dense"
                    label="Location Name"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setLocationTitle(e.target.value)}
                  />
                </MDBox>
                <MDBox mt={2}>
                  <MDInput
                    value={locationPhoneNumber}
                    name="title"
                    margin="dense"
                    label="Phone Number (Optional)"
                    type="text"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setLocationPhoneNumber(e.target.value)}
                  />
                </MDBox>
                <MDBox mt={2}>
                  <GoogleAddressField
                    displayName="Search Address On Google Maps"
                    setAddress={setLocationAddress}
                    setLatitude={setLocationLat}
                    setLongitude={setLocationLng}
                  />
                </MDBox>
                <Grid container spacing={3} mt={0}>
                  <Grid item xs={12}>
                    <MDInput
                      select
                      label="State"
                      onChange={(e) => {
                        setLocationState(e.target.value);
                      }}
                      fullWidth
                      SelectProps={{ style: { padding: "12px 0px" } }}
                      value={locationState}
                    >
                      {
                        // --> Notice below changes
                        statesLgas.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                          statesLgas.map((record) => (
                            <MenuItem value={`${record.reference}`}>
                              {record.title}
                            </MenuItem>
                          ))
                      }
                    </MDInput>
                  </Grid>
                  <Grid item xs={12}>
                    <MDInput
                      select
                      label="LGA"
                      onChange={(e) => {
                        setLocationLga(e.target.value);
                      }}
                      fullWidth
                      SelectProps={{ style: { padding: "12px 0px" } }}
                      value={locationLga}
                    >
                      {
                        // --> Notice below changes
                        lgas.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                          lgas.map((record) => (
                            <MenuItem value={`${record.reference}`}>
                              {record.title}
                            </MenuItem>
                          ))
                      }
                    </MDInput>
                  </Grid>
                  <Grid item xs={12}>
                    <MDInput
                      label="City"
                      fullWidth
                      value={locationCity}
                      onChange={(e) => setLocationCity(e.target.value)}
                    />
                  </Grid>
                </Grid>
                <MDBox mt={5}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      createLocation();
                    }}
                    fullWidth
                    startIcon={<Icon>pin_drop</Icon>}
                  >
                    Onboard Location
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default VisitLocationOnboardingPage;
